import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import Container from "../../../components/Container";
import Loader from "../../../components/Loader";
import AlertMsg from "../../../components/AlertMsg";
import MenuLayoutCard from "../components/MenuLayoutCard";
import MenuLayoutDialog from "../components/MenuLayoutDialog";
import { uiConfigServices } from "../services/menuLayoutUiConfigService";
import plusIcon from "../../../assets/images/add_plus.svg";

export const TableOrderingMenuLayout = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteConfirmData, setDeleteConfirmData] = useState(null);
  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "",
  });

  // Fetch menu layouts
  const { data: layoutsData, isLoading, refetch } = uiConfigServices.useGetMenuLayout();
  const menuLayouts = layoutsData?.data?.rows || [];

  // Create mutation
  const createMutation = uiConfigServices.useCreateMenuLayout();

  // Update mutation
  const updateMutation = uiConfigServices.useUpdateMenuLayout();

  // Delete mutation
  const deleteMutation = uiConfigServices.useDeleteMenuLayout();

  const handleOpenDialog = (data = null) => {
    setEditData(data);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditData(null);
  };

  const handleSubmit = (formData) => {
    if (editData) {
      // Update existing layout
      updateMutation.mutate(
        { id: formData.id, data: formData.data },
        {
          onSuccess: (response) => {
            setMsgAlert({
              open: true,
              message: "Menu layout updated successfully",
              msgType: "success",
            });
            refetch();
            handleCloseDialog();
          },
          onError: (error) => {
            setMsgAlert({
              open: true,
              message: error.message || "Failed to update menu layout",
              msgType: "error",
            });
          },
        }
      );
    } else {
      // Create new layout
      createMutation.mutate(formData, {
        onSuccess: (response) => {
          setMsgAlert({
            open: true,
            message: "Menu layout created successfully",
            msgType: "success",
          });
          refetch();
          handleCloseDialog();
        },
        onError: (error) => {
          setMsgAlert({
            open: true,
            message: error.message || "Failed to create menu layout",
            msgType: "error",
          });
        },
      });
    }
  };

  const handleDeleteClick = (data) => {
    setDeleteConfirmData(data);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmData?._id) {
      deleteMutation.mutate(deleteConfirmData._id, {
        onSuccess: () => {
          setMsgAlert({
            open: true,
            message: "Menu layout deleted successfully",
            msgType: "success",
          });
          refetch();
          setDeleteConfirmData(null);
        },
        onError: (error) => {
          setMsgAlert({
            open: true,
            message: error.message || "Failed to delete menu layout",
            msgType: "error",
          });
          setDeleteConfirmData(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmData(null);
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <Container
      page={"table-ordering-menu-layout"}
      searchVal={""}
      onSearch={() => null}
      searchLabel="Table Ordering Menu Layout"
    >
      {isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <>
          <Box sx={styles.headerContainer}>
            <Typography variant="h6" sx={styles.title}>
              Menu Layouts
            </Typography>
            <Button
              sx={styles.createButton}
              onClick={() => handleOpenDialog(null)}
            >
              <img src={plusIcon} alt="plus" style={{ marginRight: 5 }} />
              Create
            </Button>
          </Box>

          {menuLayouts.length > 0 ? (
            <Grid container spacing={3} sx={styles.gridContainer}>
              {menuLayouts.map((layout) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={layout._id}>
                  <MenuLayoutCard
                    data={layout}
                    onEdit={handleOpenDialog}
                    onDelete={handleDeleteClick}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={styles.emptyContainer}>
              <Typography variant="h6" sx={styles.emptyText}>
                No Menu Layouts Found
              </Typography>
              <Typography variant="body2" sx={styles.emptySubText}>
                Create your first menu layout to get started
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <MenuLayoutDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editData={editData}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmData}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={styles.dialogTitle}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={styles.dialogContent}>
            Are you sure you want to delete the layout "
            <strong>{deleteConfirmData?.name}</strong>"? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button
            onClick={handleCancelDelete}
            disabled={isDeleting}
            sx={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            sx={styles.deleteButton}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Messages */}
      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() =>
          setMsgAlert({ open: false, message: "", msgType: "" })
        }
      />
    </Container>
  );
};

export default TableOrderingMenuLayout;

const styles = {
  headerContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 3,
  },
  title: {
    fontFamily: "GilroyBold",
  },
  createButton: {
    backgroundColor: "#000",
    color: "#FFF",
    fontFamily: "GilroyBold",
    fontSize: 14,
    borderRadius: 1,
    paddingX: 2,
    "&:hover": {
      backgroundColor: "#333",
    },
  },
  gridContainer: {
    marginTop: 2,
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 400,
    textAlign: "center",
  },
  emptyText: {
    fontFamily: "GilroyBold",
    color: "#666",
    marginBottom: 1,
  },
  emptySubText: {
    fontFamily: "GilroyRegular",
    color: "#999",
  },
  dialogTitle: {
    fontFamily: "GilroyBold",
  },
  dialogContent: {
    fontFamily: "GilroyRegular",
  },
  dialogActions: {
    padding: 2,
    gap: 1,
  },
  cancelButton: {
    fontFamily: "GilroySemiBold",
    color: "#666",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.05)",
    },
  },
  deleteButton: {
    fontFamily: "GilroySemiBold",
    backgroundColor: "#d32f2f",
    color: "#FFF",
    "&:hover": {
      backgroundColor: "#b71c1c",
    },
    "&:disabled": {
      backgroundColor: "#ccc",
      color: "#666",
    },
  },
};
